/**
 * What the command bar can offer one person, as plain data.
 *
 * Kept apart from the component so the rules are testable without a modal:
 * pages come from navFor(), so a page missing from someone's rail is missing
 * here too, and each list of records is offered only to someone whose rail
 * has that list's page.
 *
 * Every item has a stable id ("page:/leads", "campaign:<id>"), which is all
 * the recent list keeps besides a label. The id alone says where it goes.
 */
import {
  IconBuilding, IconDeviceDesktop, IconFileImport, IconLock, IconMoon, IconPlus, IconSun, IconUser,
  IconFlame, IconForms, IconUserCircle, IconUsers,
} from '@tabler/icons-react';
import type { Appearance } from '../appearance';
import { APPEARANCE_OPTIONS } from '../appearance';
import { navLabel, type Vocab } from '../vocab';
import { NAV, isScopedUser, navFor, type NavEntry, type NavUser } from './nav';

type Icon = NavEntry['icon'];

export type CommandUser = (NonNullable<NavUser> & { actions?: string[] | null }) | null;

export type GroupKey =
  | 'recent'
  | 'pages'
  | 'actions'
  | 'settings'
  | 'appearance'
  | 'segments'
  | 'people'
  | 'companies'
  | 'deals'
  | 'tasks'
  | 'campaigns'
  | 'destinations'
  | 'buyers'
  | 'suppliers'
  | 'imports'
  | 'templates'
  | 'leads';

/** What choosing an item does. */
export type Run =
  | { kind: 'go'; to: string }
  | { kind: 'appearance'; value: Appearance }
  | { kind: 'lead'; id: string };

/** Extra detail a record carries for its row and the preview pane. Never contact data. */
export type CommandMeta =
  | {
    kind: 'campaign';
    id: string;
    active: boolean;
    isReference: boolean;
    groups: number;
    destinations: number;
    /** Today's count, when the dashboard already has it cached. */
    today: number | null;
  }
  | { kind: 'template'; subject: string; updatedAt: string }
  | { kind: 'segment'; id: number; count: number | null }
  | { kind: 'entity'; entity: 'buyer' | 'supplier'; id: string; active: boolean };

export type Command = {
  id: string;
  group: GroupKey;
  label: string;
  description?: string;
  keywords?: string[];
  icon: Icon;
  run: Run;
  meta?: CommandMeta;
};

/** The page each list of records lives on, which decides who may see the list. */
export const LIST_PAGE: Record<Exclude<GroupKey, 'recent' | 'pages' | 'actions' | 'settings' | 'appearance'>, string> = {
  segments: '/segments',
  people: '/people',
  companies: '/companies',
  deals: '/deals',
  tasks: '/tasks',
  campaigns: '/campaigns',
  destinations: '/destinations',
  buyers: '/buyers',
  suppliers: '/suppliers',
  imports: '/sheets',
  templates: '/emails',
  leads: '/leads',
};

export type ListKey = keyof typeof LIST_PAGE;

/** The paths of every page this person may open. */
export function visiblePaths(user: CommandUser): Set<string> {
  return new Set(navFor(user).flatMap((g) => g.items.map((i) => i.to)));
}

export function canSeeList(user: CommandUser, key: ListKey): boolean {
  return visiblePaths(user).has(LIST_PAGE[key]);
}

/** Admins can do everything; anyone else needs the action on their account. */
const ALL_ACTIONS = new Set(['super_admin', 'admin']);
/** Roles whose default action list includes these, for a session saved before `actions` was. */
const MANAGERS = new Set(['super_admin', 'admin', 'manager']);

export function canDo(user: CommandUser, action: string): boolean {
  if (!user) return false;
  if (ALL_ACTIONS.has(user.role ?? '')) return true;
  if (user.actions) return user.actions.includes(action);
  return MANAGERS.has(user.role ?? '');
}

export function iconFor(path: string): Icon {
  return NAV.flatMap((g) => g.items).find((i) => i.to === path)?.icon ?? IconPlus;
}

const APPEARANCE_ICONS: Record<Appearance, Icon> = {
  light: IconSun,
  dark: IconMoon,
  system: IconDeviceDesktop,
};

/**
 * The Settings tabs this person has, in the same words and order as the page.
 * People linking is read by anyone who may see people and changed by admins;
 * the term map names it ("People").
 */
export function settingsTabs(
  user: CommandUser, v?: Vocab,
): { value: string; label: string; icon: Icon; keywords?: string[] }[] {
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = isSuperAdmin || user?.role === 'admin';
  const people = !!user && visiblePaths(user).has('/people');
  return [
    { value: 'profile', label: 'My profile', icon: IconUser },
    { value: 'security', label: 'Sign-in and security', icon: IconLock },
    ...(isAdmin ? [{ value: 'organisation', label: isSuperAdmin ? 'Organisations' : 'Organisation', icon: IconBuilding }] : []),
    ...(people ? [{ value: 'people', label: v ? v.Many('person') : 'People', icon: IconUserCircle }] : []),
    // Fields label and type what leads carry, so they are not tied to an area either.
    ...(!!user && !isScopedUser(user) && canDo(user, 'manage_properties')
      ? [{
        value: 'fields',
        label: v ? v.Many('property') : 'Fields',
        icon: IconForms,
        keywords: ['custom fields', 'labels', 'types', 'properties'],
      }]
      : []),
    // Scoring helps distribution too (routing on a score), so it is not tied to an area.
    ...(!!user && !isScopedUser(user) && canDo(user, 'manage_scoring') ? [{ value: 'scoring', label: 'Scoring', icon: IconFlame }] : []),
    ...(isSuperAdmin ? [{ value: 'users', label: 'Everyone', icon: IconUsers }] : []),
  ];
}

/** Whether this person may see people at all: on their rail, and never a scoped login. */
export function seesPeople(user: CommandUser): boolean {
  return !!user && !isScopedUser(user) && visiblePaths(user).has('/people');
}

/**
 * Other words a page is found by, the old words among them, so someone used
 * to "campaigns" finds Streams, and "destinations" finds Routes. Keywords
 * only; the label is always the app's own word.
 */
const PAGE_KEYWORDS: Record<string, string[]> = {
  '/leads': ['rows', 'leads', 'submissions', 'records'],
  '/people': ['people', 'contacts', 'persons'],
  '/campaigns': ['campaigns', 'streams'],
  '/routes': ['routes', 'destinations', 'rotations', 'routing'],
  '/destinations': ['endpoints', 'destinations', 'webhooks'],
};

/** Pages, things to start, Settings tabs and appearance: nothing that needs a fetch. */
export function staticCommands(user: CommandUser, v: Vocab): Command[] {
  const paths = visiblePaths(user);
  const pages: Command[] = navFor(user).flatMap((g) =>
    g.items.filter((i) => i.command !== false).map((i) => ({
      id: `page:${i.to}`,
      group: 'pages' as const,
      label: navLabel(v, i.labelKey),
      ...(PAGE_KEYWORDS[i.to] ? { keywords: PAGE_KEYWORDS[i.to] } : {}),
      icon: i.icon,
      run: { kind: 'go' as const, to: i.to },
    })),
  );

  const actions: Command[] = [];
  if (paths.has('/campaigns') && canDo(user, 'edit_campaigns')) {
    actions.push({
      id: 'action:new-campaign',
      group: 'actions',
      label: `New ${v.one('campaign')}`,
      keywords: ['create', 'add'],
      icon: IconPlus,
      run: { kind: 'go', to: '/campaigns?new=1' },
    });
  }
  if (paths.has('/deals') && canDo(user, 'edit_deals')) {
    actions.push({
      id: 'action:new-deal',
      group: 'actions',
      label: `New ${v.one('deal')}`,
      keywords: ['create', 'add', v.one('pipeline')],
      icon: IconPlus,
      run: { kind: 'go', to: '/deals?new=1' },
    });
  }
  if (paths.has('/companies') && canDo(user, 'edit_companies')) {
    actions.push({
      id: 'action:new-company',
      group: 'actions',
      label: `New ${v.one('company')}`,
      keywords: ['create', 'add', 'organisation'],
      icon: IconPlus,
      run: { kind: 'go', to: '/companies?new=1' },
    });
  }
  if (paths.has('/tasks') && canDo(user, 'edit_activities')) {
    actions.push({
      id: 'action:new-task',
      group: 'actions',
      label: `New ${v.one('task')}`,
      keywords: ['create', 'add', 'todo', 'reminder', v.one('note')],
      icon: IconPlus,
      run: { kind: 'go', to: '/tasks?new=1' },
    });
  }
  if (paths.has('/automations') && canDo(user, 'manage_workflows')) {
    actions.push({
      id: 'action:new-workflow',
      group: 'actions',
      label: `New ${v.one('workflow')}`,
      keywords: ['create', 'add', 'workflow', 'automate', 'trigger'],
      icon: IconPlus,
      run: { kind: 'go', to: '/automations?new=1' },
    });
  }
  if (paths.has('/sheets') && canDo(user, 'start_imports')) {
    actions.push({
      id: 'action:new-import',
      group: 'actions',
      label: 'New import',
      description: 'Choose a file on the Imports page',
      keywords: ['upload', 'file', 'csv', 'sheet'],
      icon: IconFileImport,
      run: { kind: 'go', to: '/sheets' },
    });
  }

  // Settings has no tab key: everyone has a profile and a password.
  const settings: Command[] = paths.has('/settings')
    ? settingsTabs(user, v).map((t) => ({
      id: `settings:${t.value}`,
      group: 'settings' as const,
      label: t.label,
      description: 'Settings',
      ...(t.keywords ? { keywords: t.keywords } : {}),
      icon: t.icon,
      run: { kind: 'go' as const, to: `/settings?tab=${t.value}` },
    }))
    : [];

  const appearance: Command[] = APPEARANCE_OPTIONS.map(({ value, label }) => ({
    id: `appearance:${value}`,
    group: 'appearance' as const,
    label,
    keywords: ['appearance', 'theme', 'colour', 'color', 'mode'],
    icon: APPEARANCE_ICONS[value],
    run: { kind: 'appearance' as const, value },
  }));

  return [...pages, ...actions, ...settings, ...appearance];
}

/** Where a recent item's id leads, or null for one this version no longer knows. */
export function routeFor(id: string): string | null {
  const at = id.indexOf(':');
  if (at < 1) return null;
  const kind = id.slice(0, at);
  const rest = id.slice(at + 1);
  if (!rest) return null;
  const enc = encodeURIComponent(rest);
  switch (kind) {
    case 'page': return rest.startsWith('/') ? rest : null;
    case 'settings': return `/settings?tab=${enc}`;
    case 'campaign': return `/campaigns/${enc}`;
    case 'destination': return `/destinations/${enc}`;
    case 'sheet': return `/sheets/${enc}`;
    case 'template': return `/emails/${enc}`;
    case 'segment': return `/leads?segment=${enc}`;
    case 'company': return `/companies/${enc}`;
    case 'person': return `/people/${enc}`;
    case 'deal': return `/deals?deal=${enc}`;
    // A task has no page of its own: it opens the list it is on.
    case 'task': return '/tasks';
    // The lists have no page per record, so these open the list.
    case 'buyer': return '/buyers';
    case 'supplier': return '/suppliers';
    default: return null;
  }
}

/** The group a stored id belongs to, so a recent item is shown only to someone who may see it. */
export function groupOf(id: string): GroupKey | null {
  const kind = id.slice(0, Math.max(0, id.indexOf(':')));
  switch (kind) {
    case 'page': return 'pages';
    case 'settings': return 'settings';
    case 'campaign': return 'campaigns';
    case 'destination': return 'destinations';
    case 'sheet': return 'imports';
    case 'template': return 'templates';
    case 'segment': return 'segments';
    case 'company': return 'companies';
    case 'deal': return 'deals';
    case 'task': return 'tasks';
    case 'buyer': return 'buyers';
    case 'supplier': return 'suppliers';
    default: return null;
  }
}

/**
 * Every word of the query appears somewhere in the label, description or
 * keywords, in any order, ignoring case. "dark app" finds "Dark".
 */
export function matches(cmd: Pick<Command, 'label' | 'description' | 'keywords'>, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = [cmd.label, cmd.description ?? '', ...(cmd.keywords ?? [])].join(' ').toLowerCase();
  return words.every((w) => hay.includes(w));
}
